//React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
// Interfaces
import { Role, isRole } from 'interfaces';

// utils
import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get } from '../utils/HttpMethods';
import {
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

export const roleQueryKeys = {
  all: ['roles'],
  list: () => [...roleQueryKeys.all, 'list'],
  listByOrganization: (organizationID: string) => [
    ...roleQueryKeys.all,
    organizationID,
  ],
  get: (id: string) => [...roleQueryKeys.all, id],
};

const createRole = async (role: Role): Promise<Role> => {
  return createEntity<Role>({
    entity: role,
    path: '/resource/role',
    functionName: 'createRole',
    isEntity: isRole,
  });
};

const updateRole = async (role: Role): Promise<Role> => {
  return updateEntity<Role>({
    entity: role,
    path: '/resource/role',
    functionName: 'updateRole',
    isEntity: isRole,
  });
};

const listRoles = async (): Promise<Role[]> =>
  await get<Role[]>('QBilling', `/resource/role/all`);

export const useListRoles = (): UseQueryResult<Role[], Error> => {
  return useListQuery<Role>({
    queryKey: roleQueryKeys.list(),
    fetchFunction: listRoles,
    functionName: 'listRoles',
    isEntity: isRole,
    enabled: true,
  });
};

const getRole = async (id: string): Promise<Role | null> =>
  await get<Role>('QBilling', `/resource/role/${queryString({ id })}`);

export const useGetRole = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Role, Error> => {
  return useGetQuery<Role>({
    queryKey: roleQueryKeys.get(id),
    fetchFunction: getRole,
    functionName: 'getRole',
    params: id,
    enabled: !!id,
    isEntity: isRole,
    displayNotification,
  });
};

const listRolesByOrganization = async (
  organizationID: string
): Promise<Role[]> =>
  await get<Role[]>(
    'QBilling',
    `/resource/role/organization${queryString({ organizationID })}`
  );

export const useListRolesByOrganization = ({
  organizationID,
  displayNotification = false,
}: {
  organizationID: string;
  displayNotification?: boolean;
}): UseQueryResult<Role[], Error> => {
  return useListQuery<Role>({
    queryKey: roleQueryKeys.listByOrganization(organizationID),
    fetchFunction: listRolesByOrganization,
    functionName: 'listRolesByOrganization',
    isEntity: isRole,
    params: organizationID,
    enabled: !!organizationID,
    displayNotification,
  });
};

export const useCreateRole = (): UseMutationResult<Role, Error> => {
  return useMutationQuery<Role>({
    errorMessage: 'Role Creation Failed',
    mutationFn: createRole,
    successMessage: 'Role Created',
    queryKey: roleQueryKeys.all,
  });
};

export const useUpdateRole = (): UseMutationResult<Role, Error> => {
  return useMutationQuery<Role>({
    mutationFn: updateRole,
    errorMessage: 'Role Update Failed',
    successMessage: 'Role Updated',
    queryKey: roleQueryKeys.all,
  });
};
